// ======================================================================
// 📁 File: src/modules/ppe/PPELayout.jsx
// 📝 Purpose: PPE Module Layout (Sidebar + Content)
//        • Mirrors TicketLayout behavior exactly
//        • Uses PPE-specific sidebar + localStorage key
//        • Does NOT modify global CSS
// ======================================================================

import React, { useEffect, useState } from "react";
import PPESidebar from "./layout/PPESidebar";

/**
 * Helper: Safe fetch of CSS variable
 */
function getCssVar(varName, fallback = null) {
  try {
    const val = getComputedStyle(document.documentElement)
      .getPropertyValue(varName)
      .trim();
    return val || fallback;
  } catch {
    return fallback;
  }
}

export default function PPELayout({ children }) {

  // -------------------------------------------------------------------
  // ⭐ 1. HIDE ONLY THE IMMEDIATE PAGE HEADER ABOVE PPE MODULE
  // -------------------------------------------------------------------
  useEffect(() => {
    const selectors = [
      ".page-header",
      ".content-header",
      ".module-header",
      ".top-header",
      ".header-wrapper",
      ".page-title-wrapper",
    ];

    let found = null;
    for (const s of selectors) {
      const el = document.querySelector(s);
      if (el) {
        found = el;
        break;
      }
    }

    if (found) {
      found.dataset.originalDisplay = found.style.display || "";
      found.style.display = "none";
    }

    return () => {
      if (found) {
        found.style.display = found.dataset.originalDisplay;
      }
    };
  }, []);

  // -------------------------------------------------------------------
  // ⭐ 2. SIDEBAR COLLAPSE CONTROL (PPE-SPECIFIC)
  // -------------------------------------------------------------------
  const collapsedLS = localStorage.getItem("ppe_sidebar_collapsed");
  const initialExpanded = collapsedLS === null ? true : collapsedLS === "false";

  const [sidebarExpanded, setSidebarExpanded] = useState(initialExpanded);

  useEffect(() => {
    const handler = () => {
      const collapsed =
        localStorage.getItem("ppe_sidebar_collapsed") === "true";
      setSidebarExpanded(!collapsed);
    };
    window.addEventListener("ppe_sidebar_update", handler);
    return () =>
      window.removeEventListener("ppe_sidebar_update", handler);
  }, []);

  const handleSidebarToggle = (isExpanded) => {
    setSidebarExpanded(Boolean(isExpanded));
    localStorage.setItem(
      "ppe_sidebar_collapsed",
      isExpanded ? "false" : "true"
    );
    window.dispatchEvent(new Event("ppe_sidebar_update"));
  };

  // -------------------------------------------------------------------
  // ⭐ 3. MAIN CONTENT AREA STYLE
  // -------------------------------------------------------------------
  const expandedWidth = getCssVar("--tf-sidebar-w", "260px");
  const collapsedWidth = getCssVar("--tf-sidebar-w-collapsed", "64px");

  const contentStyle = {
    transition: "margin-left 0.22s ease, width 0.22s ease",
    marginLeft: sidebarExpanded ? expandedWidth : collapsedWidth,
    padding: "18px",
    minHeight: "100vh",
    background: "#faf7fb",
    boxSizing: "border-box",
  };

  return (
    <div className="ppe-layout-wrapper" style={{ minHeight: "100vh" }}>
      <PPESidebar onToggle={handleSidebarToggle} />

      <main className="ppe-layout-content" style={contentStyle}>
        {children}
      </main>
    </div>
  );
}
